import { useEffect, useMemo, useRef, useState } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import type { SortState } from "@/components/dataTable";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { PrintLocationDialog } from "@/features/timesheetFlow/components/PrintLocationDialog";
import { listAllOrders, type Order } from "@/lib/projects";
import { PrintJobFeedbackError, runPrintHtmlByLocationWithFeedback } from "@/lib/printJob";
import type { HeroCustomField } from "@/pages/projekte/columns";
import {
  PROJECT_PRINT_PAPERS,
  buildProjectListPrintDocument,
  formatProjectPrintCell,
  formatProjectPrintCount,
  formatProjectPrintTimestamp,
  formatProjectSortCaption,
  printableProjectColumns,
  projectPrintPageSize,
  type ProjectPrintOrientation,
  type ProjectPrintPaperId,
} from "@/pages/projekte/projectPrint";
import type { ProjectFilters } from "@/pages/projekte/useProjectFilters";
import { useBookedHours } from "@/pages/projekte/useBookedHours";
import { useHoursUsage } from "@/pages/projekte/useHoursUsage";

const CSS_PX_PER_MM = 96 / 25.4;

export function ProjectPrintPreviewDialog({
  open,
  onClose,
  filters,
  sort,
  columns,
  heroFields,
  title,
  extras,
}: {
  open: boolean;
  onClose: () => void;
  filters: ProjectFilters;
  sort: SortState | null;
  columns: string[];
  heroFields: HeroCustomField[];
  title: string;
  extras: string | null;
}) {
  const [paperId, setPaperId] = useState<ProjectPrintPaperId>("a4");
  const [orientation, setOrientation] = useState<ProjectPrintOrientation>("landscape");
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printedAt, setPrintedAt] = useState(() => formatProjectPrintTimestamp(new Date()));
  const [frameBox, setFrameBox] = useState({ html: "", height: 0 });
  const [scale, setScale] = useState(0.45);
  const [printOpen, setPrintOpen] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const printColumns = useMemo(() => printableProjectColumns(columns), [columns]);
  const needsHours = printColumns.includes("hours_usage");
  const needsBooked = printColumns.includes("booked_hours");
  const hoursById = useHoursUsage(open ? orders : [], open && needsHours);
  const bookedById = useBookedHours(open ? orders : [], open && needsBooked);

  const requestKey = useMemo(
    () =>
      JSON.stringify({
        q: filters.q,
        jahr: filters.jahr,
        kunde: filters.kunde,
        ordner: filters.ordner,
        sordner: filters.sordner,
        status: filters.status,
        sort: sort?.key ?? null,
        direction: sort?.direction ?? null,
        timesheets: printColumns.includes("timesheets"),
        customFields: printColumns.some((column) => column.startsWith("cf_")),
      }),
    [filters, sort, printColumns],
  );

  useEffect(() => {
    if (!open) setPrintOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOrders([]);
    setPrintedAt(formatProjectPrintTimestamp(new Date()));
    const params = JSON.parse(requestKey) as {
      q: string;
      jahr: ProjectFilters["jahr"];
      kunde: string;
      ordner: string;
      sordner: string;
      status: ProjectFilters["status"];
      sort: string | null;
      direction: "asc" | "desc" | null;
      timesheets: boolean;
      customFields: boolean;
    };
    void listAllOrders({
      q: params.q || undefined,
      jahr: params.jahr === null ? undefined : params.jahr,
      kunde: params.kunde || undefined,
      ordner: params.ordner || undefined,
      sordner: params.sordner || undefined,
      status: params.status.length ? params.status.join(",") : undefined,
      sort: params.sort ?? undefined,
      direction: params.direction ?? undefined,
      include_timesheets: params.timesheets,
      include_timesheet_numbers: false,
      include_hero_steps: false,
      include_tags: false,
      include_hero_custom_fields: params.customFields,
    })
      .then((rows) => {
        if (!cancelled) setOrders(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setOrders([]);
          setError(err instanceof Error ? err.message : "Die Projektliste konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, requestKey]);

  const sortCaption = formatProjectSortCaption(sort, heroFields);
  const page = projectPrintPageSize(paperId, orientation);
  const pageWidthPx = page.widthMm * CSS_PX_PER_MM;
  const pageHeightPx = page.heightMm * CSS_PX_PER_MM;

  const html = useMemo(() => {
    if (!open || loading) return "";
    const rows = orders.map((row) =>
      printColumns.map((column) =>
        formatProjectPrintCell(row, column, heroFields, hoursById[row.id], bookedById[row.id]),
      ),
    );
    return buildProjectListPrintDocument({
      title,
      extras,
      sortCaption,
      printedAt,
      countLabel: formatProjectPrintCount(orders.length),
      columns: printColumns,
      heroFields,
      rows,
      paperId,
      orientation,
    });
  }, [
    open,
    loading,
    orders,
    printColumns,
    heroFields,
    hoursById,
    bookedById,
    title,
    extras,
    sortCaption,
    printedAt,
    paperId,
    orientation,
  ]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const update = () => {
      const available = Math.max(120, stage.clientWidth - 48);
      setScale(Math.min(1, available / pageWidthPx));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [pageWidthPx, open, html]);

  function measureFrame() {
    const doc = frameRef.current?.contentDocument;
    const next = doc?.documentElement.scrollHeight ?? pageHeightPx;
    setFrameBox({ html, height: Math.max(pageHeightPx, next) });
  }

  const frameHeight = frameBox.html === html ? frameBox.height : pageHeightPx;
  const pageCount = Math.max(1, Math.ceil(frameHeight / pageHeightPx - 0.02));
  const hoursPending =
    needsHours &&
    orders.some((row) => {
      const entry = hoursById[row.id];
      return !entry || (entry.eligible && entry.loading);
    });
  const bookedPending =
    needsBooked &&
    orders.some((row) => {
      const entry = bookedById[row.id];
      return !entry || entry.loading;
    });
  const detailsPending = !loading && orders.length > 0 && (hoursPending || bookedPending);

  const htmlRef = useRef(html);
  htmlRef.current = html;

  function handlePrint() {
    if (!html) {
      setError("Die Druckvorschau ist noch nicht bereit.");
      return;
    }
    setPrintOpen(true);
  }

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      title="Druckvorschau"
      description="Alle Projekte des aktuellen Filters, in der aktuellen Sortierung."
      size="4xl"
      panelClassName="h-[min(92dvh,960px)]"
      bodyClassName="flex min-h-0 flex-col gap-3 overflow-hidden"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          <Button
            type="button"
            leftIcon={<PrinterIcon className="h-4 w-4" />}
            onClick={handlePrint}
            disabled={loading || detailsPending || !!error || orders.length === 0}
          >
            Drucken
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[9rem] space-y-1">
          <span className="eh-label">Papiergröße</span>
          <NativeSelect
            selectSize="sm"
            aria-label="Papiergröße"
            value={paperId}
            onChange={(event) => setPaperId(event.target.value as ProjectPrintPaperId)}
          >
            {PROJECT_PRINT_PAPERS.map((paper) => (
              <option key={paper.id} value={paper.id}>
                {paper.label}
              </option>
            ))}
          </NativeSelect>
        </label>
        <div className="space-y-1">
          <span className="eh-label">Ausrichtung</span>
          <SegmentedControl
            name="Ausrichtung"
            size="sm"
            value={orientation}
            onChange={setOrientation}
            options={[
              { value: "portrait", label: "Hochformat" },
              { value: "landscape", label: "Querformat" },
            ]}
          />
        </div>
        <p className="pb-2 text-sm text-label-secondary">
          {loading
            ? "Liste wird geladen …"
            : `${formatProjectPrintCount(orders.length)} · ${sortCaption} · ca. ${pageCount} ${pageCount === 1 ? "Seite" : "Seiten"}${detailsPending ? " · Stunden werden geladen …" : ""}`}
        </p>
      </div>

      {error ? (
        <Alert variant="error" title="Druckvorschau nicht verfügbar">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Gefilterte Projekte werden geladen …</span>
        </div>
      ) : error ? null : orders.length === 0 ? (
        <p className="text-sm text-label-secondary">
          Für den aktuellen Filter gibt es keine Projekte zum Drucken.
        </p>
      ) : (
        <div
          ref={stageRef}
          className="min-h-0 flex-1 overflow-auto rounded-lg bg-[#e7e7ee] p-6 dark:bg-[#2c2c32]"
        >
          <div
            className="mx-auto overflow-hidden shadow-ios-md"
            style={{ width: pageWidthPx * scale, height: frameHeight * scale }}
          >
            <iframe
              ref={frameRef}
              title="Druckvorschau der Projektliste"
              srcDoc={html}
              onLoad={measureFrame}
              className="block origin-top-left bg-white"
              style={{
                width: pageWidthPx,
                height: frameHeight,
                transform: `scale(${scale})`,
                border: 0,
              }}
            />
          </div>
        </div>
      )}
    </Dialog>

    <PrintLocationDialog
      open={printOpen}
      onClose={() => setPrintOpen(false)}
      onPrint={async (locationId, copies) => {
        try {
          await runPrintHtmlByLocationWithFeedback(locationId, copies, htmlRef.current, {
            loadingMessage: "Projektliste wird gedruckt…",
          });
        } catch (err) {
          if (err instanceof PrintJobFeedbackError) {
            throw new Error(err.feedback.message || err.feedback.title || "Druck fehlgeschlagen.");
          }
          throw err;
        }
      }}
    />
    </>
  );
}
